import { useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import { AgGridReact } from 'ag-grid-react';
import {
  CircleXIcon,
  FilterIcon,
  PrinterIcon,
  RefreshCwIcon,
  WrenchIcon,
  XIcon,
} from 'lucide-react';
import { Button } from 'primereact/button';
import { SplitButton } from 'primereact/splitbutton';
import { Toolbar as PrimeToolbar } from 'primereact/toolbar';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { donorSchedulesApi } from '@/app/services/donorSchedules';
import { setCurrentPredefinedFilter } from '@/app/slices/customViews';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { addToast } from '@/app/slices/toast';
import { parseExistingComments } from '@/components/common/revisions-button/utils';
import { SitesMultiCombobox } from '@/components/common/sites-multi-combobox';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button as BaseButton } from '@/components/ui/button';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { DonorSchedule } from '@/rtk-query.gql';
import { SiteOption } from '@/services/sites';
import { UPDATE_REVISIONS } from '@/types.gql';
import { WORKFLOW } from '@/utils/workflow';

import { SiteFilterHandle } from '../../DonorSchedulingPartnershipPage';
import {
  useContextMenuActions,
  ValidationIssue,
} from '../../hooks/useContextMenuActions';
import { ActionsSplitButton } from './ActionsSplitButton';
import { ClearFiltersButton } from './ClearFiltersButton';
import { DonorToDoReportDialog } from './DonorToDoReportDialog';
import { EditButton } from './EditButton';
import { MissingFieldsDialog } from './MissingFieldsDialog';
import { QuickFilter } from './QuickFilter';
import { ToolbarMenuButton, ToolbarMenuItem } from './ToolbarMenuButton';

import styles from './Toolbar.module.css';

const workflows = [
  WORKFLOW.Rollover2Week,
  WORKFLOW.Rollover4Week,
  WORKFLOW.SOSchedule,
  WORKFLOW.Split,
];

interface ToolbarProps {
  onSoScheduleWarning?: (validationIssues: ValidationIssue[]) => void;
  contextMenuActions: ReturnType<typeof useContextMenuActions>;
  gridRef: React.RefObject<AgGridReact<DonorSchedule>>;
  onOpenWorkflowHistory?: () => void;
  siteFilterRef?: React.MutableRefObject<SiteFilterHandle>;
}

export const Toolbar = ({
  onSoScheduleWarning,
  contextMenuActions,
  gridRef,
  onOpenWorkflowHistory,
  siteFilterRef,
}: ToolbarProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isBulkDsDeselectRowsEnabled = useFeatureFlag('Bulk_DS_Deselect_Rows');
  const isRest = useFeatureFlag('29169_donor_scheduling_to_rest');
  const isDsFilterMultiSites = useFeatureFlag('DS_Filter_Multi_Sites');
  const isSearchResetEnabled = useFeatureFlag('DS_SemenSireDonor_Search_Reset');
  const isMultiSiteFilterEnabled = isRest && isDsFilterMultiSites;
  const isBaseUiToolbar = useFeatureFlag('DS_Toolbar_Base_UI');

  const [params, setParams] = useSearchParams();
  const [selectedSites, setSelectedSites] = useState<SiteOption[]>([]);
  const { quickFilterText, selectedDonorSchedules } = useAppSelector(
    (state) => state.donorScheduling,
  );
  const [search, setSearch] = useState(
    (quickFilterText as string | undefined) ?? '',
  );

  useEffect(() => {
    if (!siteFilterRef || !isMultiSiteFilterEnabled) return;
    siteFilterRef.current = {
      getSites: () => selectedSites,
      setSites: (sites) => {
        setSelectedSites(sites);
        setParams(
          (prev) => {
            prev.delete('siteId');
            sites.forEach((s) => prev.append('siteId', s.id));
            return prev;
          },
          { replace: true },
        );
      },
    };
  }, [selectedSites, siteFilterRef, isMultiSiteFilterEnabled, setParams]);

  const handleSiteFilterChange = (sites: SiteOption[]) => {
    setSelectedSites(sites);
    setParams(
      (prev) => {
        prev.delete('siteId');

        sites.forEach((site) => {
          prev.append('siteId', site.id);
        });

        return prev;
      },
      { replace: true },
    );
  };

  const handleClearFilters = () => {
    setSearch('');
    setSelectedSites([]);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('siteId');
        return next;
      },
      { replace: true },
    );
    gridRef.current?.api?.setFilterModel(null);
    gridRef.current?.api?.onFilterChanged();
    dispatch(setCurrentPredefinedFilter(undefined));
    localStorage.removeItem('ag-grid-predefined-filter');
  };
  const { currentPredefinedFilter } = useAppSelector(
    (state) => state.currentView,
  );
  const [showAddRevisionCommentsDialog, setShowAddRevisionCommentsDialog] =
    useState(false);
  const [revisionComments, setRevisionComments] = useState<string>('');

  const isRevisionMode = params.get('revision') === 'true';
  const semenId = params.get('semenId');
  const isSemenMode = semenId !== null && semenId !== undefined;

  const [updateRevisions] = useMutation(UPDATE_REVISIONS);

  const getPredefinedFilterLabel = (filterName: string | undefined) => {
    if (!filterName) return null;

    const filterLabels: Record<string, string> = {
      oneWeekToToday: t('common.oneWeekToToday'),
      twoWeekToOneWeek: t('common.twoWeekToOneWeek'),
      today: t('common.today'),
      tomorrow: t('common.tomorrow'),
      yesterday: t('common.yesterday'),
      thisWeek: t('common.thisWeek'),
      nextWeek: t('common.nextWeek'),
      lastWeek: t('common.lastWeek'),
      nextMonth: t('common.nextMonth'),
      lastMonth: t('common.lastMonth'),
    };

    return filterLabels[filterName] || filterName;
  };

  const {
    isLoading,
    reportDialogVisible,
    setReportDialogVisible,
    missingFieldsDialogVisible,
    setMissingFieldsDialogVisible,
    handlePrintTogDonorScheduleReport,
    handlePrintDonorToDoReport,
    handleWorkflowExecute,
  } = contextMenuActions;

  const handleSetValue = async (fieldName: string, value: string) => {
    if (fieldName === 'revisionComments') {
      setRevisionComments(value);

      if (!selectedDonorSchedules || selectedDonorSchedules.length === 0) {
        return;
      }

      const newComments = parseExistingComments(value);

      const updates: Array<{
        donorScheduleId: string;
        revisionComment: string;
      }> = [];

      selectedDonorSchedules.forEach((donorSchedule: DonorSchedule) => {
        if (donorSchedule?.id) {
          const existingComments = parseExistingComments(
            donorSchedule?.revisionComment || '',
          );

          const commentsToAdd = newComments.filter(
            (newComment) => !existingComments.includes(newComment),
          );

          commentsToAdd.forEach((comment) => {
            updates.push({
              donorScheduleId: donorSchedule?.id,
              revisionComment: comment,
            });
          });
        }
      });

      if (updates.length > 0) {
        await updateRevisions({
          variables: {
            updates: updates,
          },
        });
        dispatch({
          type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
        });
      }
    }
  };

  const handleGetValue = (fieldName: string) => {
    if (fieldName === 'revisionComments') {
      if (selectedDonorSchedules && selectedDonorSchedules.length === 1) {
        const rowData = selectedDonorSchedules[0];
        return rowData?.revisionComment || '';
      }
      return revisionComments;
    }
    return null;
  };

  const handleRefreshData = () => {
    dispatch(donorSchedulesApi.util.invalidateTags(['DonorSchedules']));
    dispatch({
      type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
    });
  };

  const handleExitRevisionMode = () => {
    setParams(new URLSearchParams());
    dispatch({
      type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
    });
    dispatch(
      addToast({
        severity: 'info',
        summary: t('revision.revisionModeExited'),
        detail: t('revision.exitedRevisionModeDetail'),
        life: 3000,
      }),
    );
  };

  const handleExitSemenMode = () => {
    const newParams = new URLSearchParams(params);
    newParams.delete('semenId');
    setParams(newParams);
    dispatch(
      addToast({
        severity: 'info',
        summary: t('grid.removeSemenFilter'),
        detail: t('grid.removingSemenIdFilter'),
        life: ToastLife.DEFAULT,
      }),
    );
  };

  const handleDeselectAll = () => {
    gridRef.current?.api?.deselectAll();
    dispatch({
      type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
    });
    dispatch({
      type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
    });
  };

  const printItems: ToolbarMenuItem[] = [
    {
      label: t('print.printSoByDate'),
      icon: 'pi pi-file',
      items: [
        {
          label: 'Pdf',
          icon: 'pi pi-file-pdf',
          command() {
            setReportDialogVisible('pdf');
          },
        },
        {
          label: 'Excel',
          icon: 'pi pi-file-excel',
          command() {
            setReportDialogVisible('excel');
          },
        },
      ],
    },
    {
      label: t('print.printSoByDonor'),
      icon: 'pi pi-file',
      disabled: selectedDonorSchedules.some(
        (schedule: DonorSchedule) => schedule?.soSchedule === null,
      ),
      items: [
        {
          label: 'Pdf',
          icon: 'pi pi-file-pdf',
          command() {
            handlePrintTogDonorScheduleReport('pdf');
          },
        },
        {
          label: 'Excel',
          icon: 'pi pi-file-excel',
          command() {
            handlePrintTogDonorScheduleReport('excel');
          },
        },
      ],
    },
  ];

  const workflowItems: ToolbarMenuItem[] = [
    ...workflows.map((workflow) => ({
      label: workflow.label,
      icon: 'pi pi-play-circle',
      command: () => handleWorkflowExecute(workflow, onSoScheduleWarning),
    })),
    {
      label: t('workflowHistory.title'),
      icon: 'pi pi-history',
      disabled: selectedDonorSchedules.length !== 1,
      command: () => onOpenWorkflowHistory?.(),
    },
  ];

  const renderConfirmationDialogFooter = () => {
    return (
      <>
        <div className="rounded-lg p-3">
          <div className="flex items-center gap-2">
            <i className="pi pi-info-circle mt-0.5"></i>
            <p className="m-0 text-left text-sm">
              {
                'Complete the missing fields for each donor to proceed with the scheduling process. You can edit individual donors by double-clicking on them in the grid.'
              }
            </p>
          </div>
        </div>
      </>
    );
  };

  const dialogs = (
    <>
      <MissingFieldsDialog
        variant={isBaseUiToolbar ? 'base-ui' : 'prime'}
        visible={missingFieldsDialogVisible}
        onHide={() => setMissingFieldsDialogVisible(false)}
        selectedDonorSchedules={selectedDonorSchedules}
        renderFooter={renderConfirmationDialogFooter}
      />
      <DonorToDoReportDialog
        visible={reportDialogVisible}
        onHide={() => setReportDialogVisible(undefined)}
        onSubmit={handlePrintDonorToDoReport}
      />
    </>
  );

  const hasSelection = selectedDonorSchedules.length > 0;

  if (isBaseUiToolbar) {
    return (
      <>
        <div className="preflight flex flex-wrap items-center gap-2 pb-2">
          <div className="flex flex-wrap items-center gap-2">
            <QuickFilter
              value={search}
              onChange={setSearch}
              variant="base-ui"
            />
            {isSearchResetEnabled && (
              <ClearFiltersButton
                gridRef={gridRef}
                hasExternalFilters={
                  !!search || params.has('siteId') || !!currentPredefinedFilter
                }
                onClick={handleClearFilters}
                className="size-8"
              />
            )}
            {isMultiSiteFilterEnabled && (
              <SitesMultiCombobox
                className="min-h-8 w-72 max-w-full bg-white"
                value={selectedSites}
                onChange={handleSiteFilterChange}
                placeholder={t('site.selectFertSite')}
              />
            )}
            {currentPredefinedFilter && (
              <span className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 text-sm font-medium text-blue-800">
                <FilterIcon className="size-4 text-blue-600" />
                {getPredefinedFilterLabel(currentPredefinedFilter)}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 md:ml-auto">
            <EditButton gridRef={gridRef} variant="base-ui" />
            {isBulkDsDeselectRowsEnabled && (
              <BaseButton variant="outline" onClick={handleDeselectAll}>
                <CircleXIcon />
                {t('common.deselectAll')}
              </BaseButton>
            )}
            <ActionsSplitButton
              variant="base-ui"
              showAddRevisionCommentsDialog={showAddRevisionCommentsDialog}
              setShowAddRevisionCommentsDialog={
                setShowAddRevisionCommentsDialog
              }
              handleGetValue={handleGetValue}
              handleSetValue={handleSetValue}
              setMissingFieldsDialogVisible={setMissingFieldsDialogVisible}
            />
            <ToolbarMenuButton
              label={t('common.print')}
              icon={<PrinterIcon />}
              model={printItems}
              disabled={!hasSelection}
              loading={isLoading}
            />
            {isRevisionMode && (
              <BaseButton variant="warning" onClick={handleExitRevisionMode}>
                <XIcon />
                {t('revision.exitRevisionMode')}
              </BaseButton>
            )}
            {isSemenMode && (
              <BaseButton variant="warning" onClick={handleExitSemenMode}>
                <XIcon />
                {t('grid.removeSemenFilter')}
              </BaseButton>
            )}
          </div>

          <div className="flex items-center gap-2">
            <BaseButton
              size="icon"
              variant="outline"
              aria-label={t('common.refreshData')}
              title={t('common.refreshData')}
              onClick={handleRefreshData}
            >
              <RefreshCwIcon />
            </BaseButton>
            <ToolbarMenuButton
              label={t('common.workflows')}
              icon={<WrenchIcon />}
              model={workflowItems}
              disabled={!hasSelection}
              loading={isLoading}
              align="end"
            />
          </div>
        </div>
        {dialogs}
      </>
    );
  }

  return (
    <>
      <PrimeToolbar
        className={`${styles.toolbar} toolbar-above-panel`}
        start={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-stretch gap-2">
              <QuickFilter value={search} onChange={setSearch} />
              {isSearchResetEnabled && (
                <ClearFiltersButton
                  gridRef={gridRef}
                  hasExternalFilters={
                    !!search ||
                    params.has('siteId') ||
                    !!currentPredefinedFilter
                  }
                  onClick={handleClearFilters}
                />
              )}
            </div>
            {isMultiSiteFilterEnabled && (
              <SitesMultiCombobox
                className="donor-scheduling-fert-site-filter min-h-8 w-86 max-w-full rounded-lg border-slate-300 px-2.5 py-1 shadow-sm transition-colors focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 hover:border-slate-400 [&_[data-slot=combobox-chip-input]]:min-w-24 [&_[data-slot=combobox-chip-input]]:text-lg [&_[data-slot=combobox-chip-input]]:font-medium [&_[data-slot=combobox-chip-input]]:text-[#5e81ac] [&_[data-slot=combobox-chip-input]]:placeholder:text-slate-400"
                value={selectedSites}
                onChange={handleSiteFilterChange}
                placeholder={t('site.selectFertSite')}
              />
            )}
            {currentPredefinedFilter && (
              <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
                <i className="pi pi-filter text-blue-600"></i>
                <span className="text-sm font-medium text-blue-800">
                  {getPredefinedFilterLabel(currentPredefinedFilter)}
                </span>
              </div>
            )}
          </div>
        }
        center={
          <div className="flex flex-wrap gap-x-3 gap-y-2">
            <EditButton gridRef={gridRef} />
            {isBulkDsDeselectRowsEnabled && (
              <Button
                label={t('common.deselectAll')}
                icon="pi pi-times-circle"
                severity="secondary"
                outlined
                onClick={handleDeselectAll}
              />
            )}
            <ActionsSplitButton
              showAddRevisionCommentsDialog={showAddRevisionCommentsDialog}
              setShowAddRevisionCommentsDialog={
                setShowAddRevisionCommentsDialog
              }
              handleGetValue={handleGetValue}
              handleSetValue={handleSetValue}
              setMissingFieldsDialogVisible={setMissingFieldsDialogVisible}
            />
            <SplitButton
              label={t('common.print')}
              icon="pi pi-print"
              model={printItems}
              severity="secondary"
              disabled={selectedDonorSchedules.length < 1}
              loading={isLoading}
            />
            {isRevisionMode && (
              <Button
                icon="pi pi-times"
                label={t('revision.exitRevisionMode')}
                severity="warning"
                onClick={handleExitRevisionMode}
              />
            )}
            {isSemenMode && (
              <Button
                icon="pi pi-times"
                label={t('grid.removeSemenFilter')}
                severity="warning"
                onClick={handleExitSemenMode}
              />
            )}
          </div>
        }
        end={
          <>
            <Button
              icon="pi pi-refresh"
              aria-label={t('common.refreshData')}
              onClick={handleRefreshData}
              severity="secondary"
            />
            <SplitButton
              label={t('common.workflows')}
              icon="pi pi-wrench"
              model={workflowItems}
              severity="secondary"
              disabled={selectedDonorSchedules.length < 1}
              loading={isLoading}
            />
          </>
        }
      />
      {dialogs}
    </>
  );
};
